/**
 * WebAuthn / Passkey helpers.
 *
 * All the base64url ↔ ArrayBuffer plumbing that the browser needs to talk
 * to navigator.credentials lives here so the pages stay small.
 */
import api from '../services/api';
import { storeAuth } from '../services/auth';

// --- base64url helpers ------------------------------------------------

function b64urlToArrayBuffer(b64url) {
  const b64 = b64url.replace(/-/g, '+').replace(/_/g, '/');
  const pad = '='.repeat((4 - (b64.length % 4)) % 4);
  const bin = window.atob(b64 + pad);
  const buf = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) buf[i] = bin.charCodeAt(i);
  return buf.buffer;
}

function arrayBufferToB64url(buf) {
  const bytes = new Uint8Array(buf);
  let bin = '';
  for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
  return window.btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

// Recursively converts base64url strings in known-shape credential
// creation/request options into ArrayBuffers, since navigator.credentials
// expects real binary values, not strings.
function decodeCreationOptions(opts) {
  return {
    ...opts,
    challenge: b64urlToArrayBuffer(opts.challenge),
    user: { ...opts.user, id: b64urlToArrayBuffer(opts.user.id) },
    excludeCredentials: (opts.excludeCredentials || []).map((c) => ({
      ...c, id: b64urlToArrayBuffer(c.id),
    })),
  };
}

function decodeRequestOptions(opts) {
  return {
    ...opts,
    challenge: b64urlToArrayBuffer(opts.challenge),
    allowCredentials: (opts.allowCredentials || []).map((c) => ({
      ...c, id: b64urlToArrayBuffer(c.id),
    })),
  };
}

// Encodes a PublicKeyCredential response into the JSON the backend expects.
function encodeCredential(cred) {
  const resp = cred.response;
  const encoded = {
    id: cred.id,
    rawId: arrayBufferToB64url(cred.rawId),
    type: cred.type,
    response: {},
  };
  if (resp.attestationObject) {
    encoded.response.attestationObject = arrayBufferToB64url(resp.attestationObject);
    encoded.response.clientDataJSON = arrayBufferToB64url(resp.clientDataJSON);
  } else {
    encoded.response.authenticatorData = arrayBufferToB64url(resp.authenticatorData);
    encoded.response.clientDataJSON = arrayBufferToB64url(resp.clientDataJSON);
    encoded.response.signature = arrayBufferToB64url(resp.signature);
    if (resp.userHandle) {
      encoded.response.userHandle = arrayBufferToB64url(resp.userHandle);
    }
  }
  return encoded;
}

// --- Public API -------------------------------------------------------

export function isWebAuthnSupported() {
  return typeof window !== 'undefined'
    && !!window.PublicKeyCredential
    && typeof window.PublicKeyCredential === 'function';
}

/**
 * Enroll the current device as a passkey. Requires the user to already be
 * logged in (any method) so we know who to bind the passkey to.
 */
export async function enrollPasskey(deviceName) {
  const { data } = await api.post('/webauthn/register/options');
  const opts = JSON.parse(data.options);
  const publicKey = decodeCreationOptions(opts);
  const cred = await navigator.credentials.create({ publicKey });
  if (!cred) throw new Error('No credential returned');
  const encoded = encodeCredential(cred);
  await api.post('/webauthn/register/verify', {
    challenge_token: data.challenge_token,
    credential: encoded,
    device_name: deviceName || 'This device',
  });
  return { device_name: deviceName || 'This device' };
}

/**
 * Log in via an already-enrolled passkey. Prompts Face ID / Touch ID
 * automatically on the user's device.
 */
export async function loginWithPasskey(email) {
  const { data } = await api.post('/webauthn/authenticate/options', { email });
  const opts = JSON.parse(data.options);
  const publicKey = decodeRequestOptions(opts);
  const cred = await navigator.credentials.get({ publicKey });
  if (!cred) throw new Error('No credential returned by the browser');
  const encoded = encodeCredential(cred);
  const verifyRes = await api.post('/webauthn/authenticate/verify', {
    challenge_token: data.challenge_token,
    credential: encoded,
    keep_logged_in: true,
  });
  const { token, user } = verifyRes.data;
  storeAuth(token, user, true);
  return { user };
}

export async function listPasskeys() {
  const { data } = await api.get('/webauthn/credentials');
  return data.credentials || [];
}

export async function deletePasskey(id) {
  await api.delete(`/webauthn/credentials/${id}`);
}
